import { DocumentType, parseDateString, TIdentityDocumentType } from '@verifime/utils';
import { format, isValid } from 'date-fns';
import { toImmiCardPayload } from './immiCardPayload';
import { toPhotoCardPayload } from './photoCardPayload';
import { TIdentityDocumentData, TOcrData } from './types';

// Where each document type keeps its fields in the form data, and which of them is the expiry.
const PAYLOAD_SOURCE_BY_DOCUMENT_TYPE: Partial<
  Record<TIdentityDocumentType, { dataKey: keyof TOcrData; expiryField: string }>
> = {
  [DocumentType.Passport]: { dataKey: 'passport', expiryField: 'passportExpiry' },
  [DocumentType.DriverLicence]: { dataKey: 'driverLicence', expiryField: 'licenceExpiry' },
  [DocumentType.MedicareCard]: { dataKey: 'medicareCard', expiryField: 'medicareCardExpiry' },
};

// The fields that hold a person's name. The form rules trim before they check (yup's `.trim()`
// transform), so "  Jane " passes validation, but the form keeps the value as typed; trimming it
// here is what makes the name that was validated the name that is sent.
const NAME_FIELDS = [
  'passportGivenNames',
  'passportFamilyName',
  'licenceFirstName',
  'licenceMiddleName',
  'licenceFamilyName',
  'medicareCardFullName',
];

function trimNames(documentData: Record<string, unknown>): Record<string, unknown> {
  const trimmed = { ...documentData };
  for (const field of NAME_FIELDS) {
    const value = trimmed[field];
    if (typeof value === 'string') {
      trimmed[field] = value.trim();
    }
  }
  return trimmed;
}

/**
 * The expiry as yyyy-MM-dd. It throws when the expiry is not a real date. The expiry is parsed once,
 * with the parse the form's own expiry rule uses (parseDateString), and that same local date is
 * formatted, so the day that was validated is the day that is sent. `new Date('2030-03-15')` would
 * read the string as UTC midnight, which is the day before in a zone behind UTC.
 */
function formatExpiry(expiry: unknown): string {
  // parseDateString is typed `string | Date` but always returns a Date, an invalid one when the
  // value is not a date.
  const date = parseDateString('', expiry as string) as Date;
  if (!isValid(date)) {
    throw new Error('The expiry is not a date');
  }
  return format(date, 'yyyy-MM-dd');
}

/**
 * Builds the document body the wizard submits to person-api. It throws when the document type has
 * no payload here or the form holds no object for it, and it refuses an expiry that is not a real
 * date, which is what stops a form with every field blank (a blank expiry is not a date). It trims
 * the name fields.
 *
 * The expiry is sent as yyyy-MM-dd. The date picker holds it as a Date, and the Zodios client
 * validates the body before it sends the request, so a Date where the contract declares a string
 * fails there ("Zodios: Invalid Body parameter", caused by "Expected string, received date") and
 * no request is made.
 */
export function buildIdentityDocumentPayload(
  documentType: TIdentityDocumentType,
  formData: TOcrData | null,
): TIdentityDocumentData {
  // A photo card has its own payload function: its expiry is optional (two templates print none),
  // so it is left out when absent rather than refused.
  if (documentType === DocumentType.PhotoCard) {
    if (!formData?.photoCard) {
      throw new Error(`No ${documentType} form data to submit`);
    }
    return toPhotoCardPayload(formData.photoCard) as TIdentityDocumentData;
  }

  // An ImmiCard has its own payload function: the number is normalised, and its expiry is optional
  // and left out when it is not a real date.
  if (documentType === DocumentType.ImmiCard) {
    if (!formData?.immiCard) {
      throw new Error(`No ${documentType} form data to submit`);
    }
    return toImmiCardPayload(formData.immiCard) as TIdentityDocumentData;
  }

  const source = PAYLOAD_SOURCE_BY_DOCUMENT_TYPE[documentType];
  if (!source) {
    throw new Error(`No identity document payload for document type ${documentType}`);
  }

  const documentData = formData?.[source.dataKey] as Record<string, unknown> | undefined;
  if (!documentData) {
    throw new Error(`No ${documentType} form data to submit`);
  }

  return {
    ...trimNames(documentData),
    [source.expiryField]: formatExpiry(documentData[source.expiryField]),
  } as TIdentityDocumentData;
}
